import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";

interface Props {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  employeeId: string;
  onClose: () => void;
  onConfirmed: (photoPath: string | null) => Promise<void> | void;
}

type CameraStatus = "requesting" | "ready" | "denied" | "unavailable";

export function WebcamConfirmModal({ open, title, description, confirmLabel, employeeId, onClose, onConfirmed }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>("requesting");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) void initCamera();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function initCamera() {
    setCameraStatus("requesting");
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraStatus("unavailable");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
        audio: false,
      });
      streamRef.current = stream;
      setCameraStatus("ready");
    } catch {
      setCameraStatus("denied");
    }
  }

  function stopCamera() {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }

  function attachVideo(el: HTMLVideoElement | null) {
    videoRef.current = el;
    if (el && streamRef.current) {
      el.srcObject = streamRef.current;
      void el.play().catch(() => {});
    }
  }

  async function capturePhoto(): Promise<Blob | null> {
    const v = videoRef.current;
    if (!v || !streamRef.current || cameraStatus !== "ready") return null;
    if (v.videoWidth === 0 || v.videoHeight === 0) return null;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob | null>(res => canvas.toBlob(b => res(b), "image/jpeg", 0.75));
  }

  async function uploadPhoto(blob: Blob): Promise<string | null> {
    const ymd = new Date().toISOString().slice(0, 10);
    const path = `${ymd}/${employeeId}-${Date.now()}.jpg`;
    const { error } = await supabase.storage.from("kiosk-photos").upload(path, blob, {
      contentType: "image/jpeg",
      upsert: false,
    });
    if (error) return null;
    return path;
  }

  async function confirm() {
    setBusy(true); setError(null);
    let photoPath: string | null = null;
    if (cameraStatus === "ready") {
      const blob = await capturePhoto();
      if (blob) photoPath = await uploadPhoto(blob);
    }
    try {
      await onConfirmed(photoPath);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
      setBusy(false);
      return;
    }
    setBusy(false);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-soft"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="mb-1 text-xl font-bold">{title}</h2>
        {description && <p className="mb-4 text-sm text-gray2">{description}</p>}

        <div className="mb-4 overflow-hidden rounded-xl bg-ink/90 ring-1 ring-line aspect-[4/3] relative">
          {cameraStatus === "ready" ? (
            <video ref={attachVideo} playsInline muted autoPlay className="h-full w-full object-cover scale-x-[-1]" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-center text-white">
              <div>
                <div className="text-4xl">📷</div>
                <div className="mt-2 text-sm">
                  {cameraStatus === "denied" ? "Camera access denied" :
                   cameraStatus === "unavailable" ? "Camera not available — clock will still record" :
                   "Starting camera…"}
                </div>
                {cameraStatus === "denied" && (
                  <button onClick={initCamera} className="mt-2 text-xs underline">Retry</button>
                )}
              </div>
            </div>
          )}
          {cameraStatus === "ready" && (
            <div className="absolute bottom-2 right-2 rounded bg-bad/80 px-2 py-0.5 text-xs font-semibold text-white">● REC</div>
          )}
        </div>

        {error && <div className="mb-3 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{error}</div>}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-ghost" disabled={busy}>Cancel</button>
          <button onClick={confirm} className="btn" disabled={busy}>
            {busy ? "…" : confirmLabel}
          </button>
        </div>
        {cameraStatus !== "ready" && cameraStatus !== "requesting" && (
          <p className="mt-3 text-center text-xs text-gray2">
            No photo will be captured — clock action will still be recorded.
          </p>
        )}
      </div>
    </div>
  );
}
